import { forwardRef, KeyboardEvent, ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import Button from "../UI/Button";
import FileUpload from "../UI/FileUpload";
import Input from "../UI/Input";
import Loader from "../UI/Loader";
import Modal from "../UI/Modal";
import * as fileScanStatus from "../../constants/fileScanStatus";
import * as fileUploadStatus from "../../constants/fileUploadStatus";
import UploadableFile from "../../models/pageModels/UploadableFile";
import FormInstance from "../../models/serviceModels/FormInstance";
import {
    PublicSubmissionFollowUp,
    PublicSubmissionFollowUpsResponse
} from "../../models/serviceModels/PublicSubmissionFollowUp";
import {
    cancelPublicSubmissionFollowUp,
    createPublicSubmissionFollowUp,
    getPublicSubmissionFollowUps,
    reactivatePublicSubmissionFollowUp
} from "../../services/formsService";
import {
    buildPublicSubmissionFollowUpLink,
    buildPublicSubmissionFollowUpMailto,
    getPublicSubmissionParticipantEmail
} from "../../utils/publicSubmissionFollowUp";
import { isValidEmail } from "../../utils/validation/emailValidation";
import './PublicSubmissionDetailTabs.css';

interface PublicSubmissionDetailTabsProps {
    formInstanceId: string;
    formName: string;
    instance: FormInstance;
    processed: boolean;
    readOnly?: boolean;
    originalContent: ReactNode;
    onFollowUpsChanged: (response: PublicSubmissionFollowUpsResponse) => void;
}

export interface PublicSubmissionDetailTabsHandle {
    openRequestDocumentation: () => void;
}

const MAX_REQUEST_LENGTH = 10000;

const toUploadableFiles = (followUp: PublicSubmissionFollowUp): UploadableFile[] => followUp.files.map(file => ({
    fileId: file.fileId,
    name: file.name,
    created: file.created,
    status: file.status === fileScanStatus.CLEAN ? fileUploadStatus.UPLOADED :
        file.status === fileScanStatus.INFECTED ? fileUploadStatus.INFECTED :
            fileUploadStatus.SCANNING
}));

const PublicSubmissionDetailTabs = forwardRef<PublicSubmissionDetailTabsHandle, PublicSubmissionDetailTabsProps>((props, ref) => {
    const [response, setResponse] = useState<PublicSubmissionFollowUpsResponse>();
    const [activeTab, setActiveTab] = useState('original');
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<any>();
    const [requestText, setRequestText] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [cancelFollowUp, setCancelFollowUp] = useState<PublicSubmissionFollowUp>();
    const [cancelling, setCancelling] = useState(false);
    const [reactivatingId, setReactivatingId] = useState<string>();
    const [copiedId, setCopiedId] = useState<string>();
    const [emailFollowUp, setEmailFollowUp] = useState<PublicSubmissionFollowUp>();
    const [recipientEmail, setRecipientEmail] = useState('');
    const [recipientEmailError, setRecipientEmailError] = useState('');
    const onFollowUpsChangedRef = useRef(props.onFollowUpsChanged);

    const participantEmail = useMemo(() => getPublicSubmissionParticipantEmail(props.instance), [props.instance]);

    useEffect(() => {
        onFollowUpsChangedRef.current = props.onFollowUpsChanged;
    }, [props.onFollowUpsChanged]);

    useImperativeHandle(ref, () => ({
        openRequestDocumentation: () => {
            if (!props.processed && !props.readOnly) {
                setShowCreate(true);
            }
        }
    }), [props.processed, props.readOnly]);

    const updateResponse = (nextResponse: PublicSubmissionFollowUpsResponse) => {
        setResponse(nextResponse);
        onFollowUpsChangedRef.current(nextResponse);
    };

    const buildResponse = (followUps: PublicSubmissionFollowUp[]): PublicSubmissionFollowUpsResponse => {
        const openCount = followUps.filter(followUp => followUp.status === 'OPEN').length;
        const submittedCount = followUps.filter(followUp => followUp.status === 'SUBMITTED').length;
        return {
            followUps,
            canResolve: openCount === 0,
            followUpStatus: openCount > 0 ? 'AWAITING_RESPONSE' : submittedCount > 0 ? 'READY_FOR_REVIEW' : 'NONE'
        };
    };

    useEffect(() => {
        setLoading(true);
        setApiError(undefined);
        setActiveTab('original');
        getPublicSubmissionFollowUps(props.formInstanceId).then(result => {
            setResponse(result);
            onFollowUpsChangedRef.current(result);
        }).catch(error => {
            setApiError(error);
        }).finally(() => {
            setLoading(false);
        });
    }, [props.formInstanceId]);

    const createRequest = () => {
        const normalizedText = requestText.trim();
        if (!normalizedText) {
            return;
        }

        setCreating(true);
        setApiError(undefined);
        createPublicSubmissionFollowUp(props.formInstanceId, normalizedText).then(result => {
            const nextFollowUps = [...(response?.followUps || []), result.followUp]
                .sort((left, right) => left.sequence - right.sequence);
            updateResponse(buildResponse(nextFollowUps));
            setActiveTab(result.followUp.id);
            setRequestText('');
            setShowCreate(false);
        }).catch(error => {
            setApiError(error);
        }).finally(() => {
            setCreating(false);
        });
    };

    const reactivateRequest = (followUp: PublicSubmissionFollowUp) => {
        setReactivatingId(followUp.id);
        setApiError(undefined);
        reactivatePublicSubmissionFollowUp(props.formInstanceId, followUp.id).then(() => {
            const nextFollowUps = (response?.followUps || []).map(item => item.id === followUp.id ? {
                ...item,
                status: 'OPEN' as const,
                cancelled: undefined
            } : item);
            updateResponse(buildResponse(nextFollowUps));
        }).catch(error => {
            setApiError(error);
        }).finally(() => {
            setReactivatingId(undefined);
        });
    };

    const cancelRequest = () => {
        if (!cancelFollowUp) {
            return;
        }

        setCancelling(true);
        setApiError(undefined);
        cancelPublicSubmissionFollowUp(props.formInstanceId, cancelFollowUp.id).then(() => {
            const nextFollowUps = (response?.followUps || []).map(followUp => followUp.id === cancelFollowUp.id ? {
                ...followUp,
                status: 'CANCELLED' as const,
                cancelled: new Date().toISOString()
            } : followUp);
            updateResponse(buildResponse(nextFollowUps));
            setCancelFollowUp(undefined);
        }).catch(error => {
            setApiError(error);
        }).finally(() => {
            setCancelling(false);
        });
    };

    const getLink = (followUp?: PublicSubmissionFollowUp) => {
        if (!followUp?.publicId) {
            return '';
        }
        return buildPublicSubmissionFollowUpLink(followUp.publicId);
    };

    const copyLink = async (followUp: PublicSubmissionFollowUp) => {
        const link = getLink(followUp);
        if (!link) {
            return;
        }

        try {
            await navigator.clipboard.writeText(link);
            setCopiedId(followUp.id);
            setTimeout(() => setCopiedId(current => current === followUp.id ? undefined : current), 3000);
        } catch (error) {
            console.error(error);
            toast.error('Unable to copy the link. Select and copy it manually.');
        }
    };

    const openEmail = (followUp: PublicSubmissionFollowUp, recipient: string) => {
        const link = getLink(followUp);
        if (!link) {
            return;
        }

        const anchor = document.createElement('a');
        anchor.href = buildPublicSubmissionFollowUpMailto(recipient, props.formName, link);
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.click();
    };

    const emailParticipant = (followUp: PublicSubmissionFollowUp) => {
        if (!participantEmail) {
            setRecipientEmail('');
            setRecipientEmailError('');
            setEmailFollowUp(followUp);
            return;
        }

        openEmail(followUp, participantEmail);
    };

    const emailEnteredRecipient = () => {
        if (!emailFollowUp) {
            return;
        }

        const normalizedEmail = recipientEmail.trim();
        if (!isValidEmail(normalizedEmail)) {
            setRecipientEmailError('Enter a valid email address.');
            return;
        }

        openEmail(emailFollowUp, normalizedEmail);
        setEmailFollowUp(undefined);
    };

    const followUps = response?.followUps || [];
    const tabIds = ['original', ...followUps.map(followUp => followUp.id)];
    const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tabId: string) => {
        const currentIndex = tabIds.indexOf(tabId);
        let nextIndex = currentIndex;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabIds.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabIds.length) % tabIds.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabIds.length - 1;
        if (nextIndex !== currentIndex) {
            event.preventDefault();
            setActiveTab(tabIds[nextIndex]);
            document.getElementById(`public-submission-tab-${tabIds[nextIndex]}`)?.focus();
        }
    };

    const activeFollowUp = followUps.find(followUp => followUp.id === activeTab);

    return <div className="public-submission-tabs-container">
        <ApiErrorMessages error={apiError} />
        <div className="row justify-content-between align-items-center margin-vertical-3">
            <div className="column-auto">
                <h3>Submission history</h3>
            </div>
        </div>
        {loading && <Loader />}
        {!loading && <>
            <div className="public-submission-tabs" role="tablist" aria-label="Public submission history">
                <button
                    id="public-submission-tab-original"
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'original'}
                    aria-controls="public-submission-panel-original"
                    tabIndex={activeTab === 'original' ? 0 : -1}
                    className={activeTab === 'original' ? 'active' : ''}
                    onClick={() => setActiveTab('original')}
                    onKeyDown={event => handleTabKeyDown(event, 'original')}>
                    Original Submission
                </button>
                {followUps.map(followUp => <button
                    id={`public-submission-tab-${followUp.id}`}
                    key={followUp.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === followUp.id}
                    aria-controls={`public-submission-panel-${followUp.id}`}
                    tabIndex={activeTab === followUp.id ? 0 : -1}
                    className={activeTab === followUp.id ? 'active' : ''}
                    onClick={() => setActiveTab(followUp.id)}
                    onKeyDown={event => handleTabKeyDown(event, followUp.id)}>
                    Follow-up {followUp.sequence}
                    <span className={`follow-up-tab-status ${followUp.status.toLowerCase()}`}>{followUp.status === 'OPEN' ? 'Open' : followUp.status === 'SUBMITTED' ? 'Received' : 'Cancelled'}</span>
                </button>)}
            </div>
            <div
                id="public-submission-panel-original"
                role="tabpanel"
                aria-labelledby="public-submission-tab-original"
                hidden={activeTab !== 'original'}>
                {activeTab === 'original' && props.originalContent}
            </div>
            {activeFollowUp && <div
                id={`public-submission-panel-${activeFollowUp.id}`}
                role="tabpanel"
                aria-labelledby={`public-submission-tab-${activeFollowUp.id}`}
                className="public-submission-follow-up-panel">
                <div className="row justify-content-between align-items-start">
                    <div className="column">
                        <div className="text-bold">Requested {format(new Date(activeFollowUp.created), 'Pp')}</div>
                        {activeFollowUp.createdBy?.displayName && <div className="text-sub">By {activeFollowUp.createdBy.displayName}</div>}
                    </div>
                    <div className="column-auto">
                        <span className={`follow-up-status ${activeFollowUp.status.toLowerCase()}`}>{activeFollowUp.status === 'OPEN' ? 'Awaiting response' : activeFollowUp.status === 'SUBMITTED' ? 'Documentation received' : 'Cancelled'}</span>
                    </div>
                </div>
                <h4>Request</h4>
                <div className="follow-up-text">{activeFollowUp.requestText}</div>
                {!props.readOnly && <div className="margin-top-5">
                    {activeFollowUp.status === 'OPEN' && <Input id={`follow-up-public-link-${activeFollowUp.id}`} label="Single-use public link" value={getLink(activeFollowUp)} readOnly={true} />}
                    {activeFollowUp.status === 'SUBMITTED' && <p className="text-sub">This single-use public link expired when the documentation was submitted.</p>}
                    {activeFollowUp.status === 'CANCELLED' && <p className="text-sub">This public link is inactive while the request is cancelled.</p>}
                    <div className="row margin-top-3">
                        {activeFollowUp.status === 'OPEN' && <div className="column-auto">
                            <Button id={`copy-follow-up-link-${activeFollowUp.id}`} text={copiedId === activeFollowUp.id ? 'Copied' : 'Copy Link'} className="small secondary" onClick={() => copyLink(activeFollowUp)} />
                        </div>}
                        {activeFollowUp.status === 'OPEN' && <>
                            <div className="column-auto">
                                <Button id={`email-follow-up-${activeFollowUp.id}`} text="Email Participant" className="small secondary" onClick={() => emailParticipant(activeFollowUp)} />
                                <div className="text-sub margin-top-1">Opens your email app—you send the message.</div>
                            </div>
                            <div className="column-auto">
                                <Button id={`cancel-follow-up-${activeFollowUp.id}`} text="Cancel Request" className="small warning" onClick={() => setCancelFollowUp(activeFollowUp)} />
                            </div>
                        </>}
                        {activeFollowUp.status === 'CANCELLED' &&
                            activeFollowUp.id === followUps[followUps.length - 1]?.id &&
                            !props.processed && <div className="column-auto">
                            <Button
                                id={`reactivate-follow-up-${activeFollowUp.id}`}
                                text="Reactivate Request"
                                className="small secondary"
                                loading={reactivatingId === activeFollowUp.id}
                                disabled={!!reactivatingId}
                                onClick={() => reactivateRequest(activeFollowUp)} />
                        </div>}
                    </div>
                </div>}
                {activeFollowUp.status === 'SUBMITTED' && <>
                    <h4>Response</h4>
                    {activeFollowUp.responseNote && <div className="follow-up-text">{activeFollowUp.responseNote}</div>}
                    {activeFollowUp.files.length > 0 && <div className="margin-top-3">
                        <FileUpload
                            id={`follow-up-files-${activeFollowUp.id}`}
                            value={toUploadableFiles(activeFollowUp)}
                            disableUpload={true}
                            disableRemove={true} />
                    </div>}
                    {activeFollowUp.submitted && <div className="text-sub margin-top-3">Received {format(new Date(activeFollowUp.submitted), 'Pp')}</div>}
                </>}
                {activeFollowUp.status === 'CANCELLED' && activeFollowUp.cancelled && <div className="text-sub margin-top-3">Cancelled {format(new Date(activeFollowUp.cancelled), 'Pp')}</div>}
            </div>}
        </>}
        <Modal id="create-follow-up-request" visible={showCreate} className="small" title="Request Documentation" onClose={() => !creating && setShowCreate(false)}>
            <Input
                id="follow-up-request-text"
                type="multi"
                label="Describe the documents and information needed. Do not include sensitive information in your request."
                required={true}
                value={requestText}
                maxLength={MAX_REQUEST_LENGTH}
                onChange={setRequestText} />
            <p className="text-sub">A shareable link will be created. Countbubble will not email it to the recipient for you.</p>
            <div className="row justify-content-between margin-top-5">
                <div className="column-auto"><Button id="cancel-create-follow-up" text="Cancel" className="secondary" disabled={creating} onClick={() => setShowCreate(false)} /></div>
                <div className="column-auto"><Button id="create-follow-up" text="Create Request" loading={creating} disabled={creating || !requestText.trim()} onClick={createRequest} /></div>
            </div>
        </Modal>
        <Modal id="cancel-follow-up-request" visible={!!cancelFollowUp} className="small" title="Cancel Documentation Request" onClose={() => !cancelling && setCancelFollowUp(undefined)}>
            <p>The existing link will stop accepting a response. This request will remain in the submission history.</p>
            <div className="row justify-content-between margin-top-5">
                <div className="column-auto"><Button id="keep-follow-up-request" text="Keep Request" className="secondary" disabled={cancelling} onClick={() => setCancelFollowUp(undefined)} /></div>
                <div className="column-auto"><Button id="confirm-cancel-follow-up" text="Cancel Request" className="warning" loading={cancelling} disabled={cancelling} onClick={cancelRequest} /></div>
            </div>
        </Modal>
        <Modal id="enter-follow-up-email" visible={!!emailFollowUp} className="small" title="Email Participant" onClose={() => setEmailFollowUp(undefined)}>
            <p>No participant email was included with this submission. Enter an email address to open a pre-addressed message with the documentation link.</p>
            <Input
                id="follow-up-recipient-email"
                label="Recipient Email"
                type="email"
                required={true}
                value={recipientEmail}
                onChange={value => {
                    setRecipientEmail(value);
                    setRecipientEmailError('');
                }} />
            {recipientEmailError && <div className="error-message">{recipientEmailError}</div>}
            <div className="row justify-content-between margin-top-5">
                <div className="column-auto"><Button id="cancel-follow-up-email" text="Cancel" className="secondary" onClick={() => setEmailFollowUp(undefined)} /></div>
                <div className="column-auto"><Button id="send-follow-up-email" text="Open Email" disabled={!recipientEmail.trim()} onClick={emailEnteredRecipient} /></div>
            </div>
        </Modal>
    </div>;
});

PublicSubmissionDetailTabs.displayName = 'PublicSubmissionDetailTabs';

export default PublicSubmissionDetailTabs;
